import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaUserGraduate } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../academicConfiguration/AcademicCreate.module.css";
import radioCheckboxStyles from "./Radiocheckbox.module.css";
import {
  CustomButton,
  CustomDatePicker,
  CustomDropdown,
  CustomTextInput,
  CustomFileInput,
} from "../../../../components";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import BlobImage from "../../../../components/BlobImage/BlobImage";
import {
  validateStudentForm,
  formatMobileInput,
  formatAadharInput,
  formatPincodeInput,
} from "./Studentvalidation";
import ParentInformation from "./Parentinformation";
import EmergencyContact from "./Emergencycontact";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchIncomeOptions,
  fetchOccupationOptions,
  fetchBloodGroupOptions,
  fetchCategoryOptions,
  fetchReligionOptions,
  fetchRelationOptions,
  fetchClassOptions,
  fetchSectionOptions,
  fetchCountryOptions,
  fetchStateOptions,
  clearSectionOptions,
  clearStateOptions,
  STATE_TYPES,
  GENDER_OPTIONS,
} from "../../../../redux/Masterdata";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const StudentCreate = () => {
  const { translations } = useSelector((state) => state.language);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.ID || null;

  // Get user data from TokenService
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const user_id = user?.id || "";
  const roleId = Number(user?.role?.id);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errors, setErrors] = useState({});
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // Delete Confirmation Modal
  const [deleteModalState, setDeleteModalState] = useState({
    open: false,
  });

  // Get master data from Redux store
  const {
    incomeOptions: reduxIncomeOptions,
    occupationOptions: reduxOccupationOptions,
    bloodGroupOptions: reduxBloodGroupOptions,
    categoryOptions: reduxCategoryOptions,
    religionOptions: reduxReligionOptions,
    relationOptions: reduxRelationOptions,
    classOptions: reduxClassOptions,
    sectionOptions: reduxSectionOptions,
    countryOptions: reduxCountryOptions,
    fatherStateOptions: reduxFatherStateOptions,
    motherStateOptions: reduxMotherStateOptions,
    guardianStateOptions: reduxGuardianStateOptions,
  } = useSelector((state) => state.masterData);

  // Memoized dropdown options with default empty option
  const bloodGroupOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectBloodGroup || "Select Blood Group",
      },
      ...reduxBloodGroupOptions,
    ],
    [reduxBloodGroupOptions, translations],
  );

  const classOptions = useMemo(
    () => [
      { value: "", label: translations.labels?.selectClass || "Select Class" },
      ...reduxClassOptions,
    ],
    [reduxClassOptions, translations],
  );

  const sectionOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectSection || "Select Section",
      },
      ...reduxSectionOptions,
    ],
    [reduxSectionOptions, translations],
  );

  const religionOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectReligion || "Select Religion",
      },
      ...reduxReligionOptions,
    ],
    [reduxReligionOptions, translations],
  );

  const categoryOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectCategory || "Select Category",
      },
      ...reduxCategoryOptions,
    ],
    [reduxCategoryOptions, translations],
  );

  const occupationOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectOccupation || "Select Occupation",
      },
      ...reduxOccupationOptions,
    ],
    [reduxOccupationOptions, translations],
  );

  const incomeOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectIncome || "Select Income",
      },
      ...reduxIncomeOptions,
    ],
    [reduxIncomeOptions, translations],
  );

  const relationTypeOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectRelation || "Select Relation",
      },
      ...reduxRelationOptions,
    ],
    [reduxRelationOptions, translations],
  );

  const countryOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectCountry || "Select Country",
      },
      ...reduxCountryOptions,
    ],
    [reduxCountryOptions, translations],
  );

  const fatherStateOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectState || "Select State",
      },
      ...reduxFatherStateOptions,
    ],
    [reduxFatherStateOptions, translations],
  );

  const motherStateOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectState || "Select State",
      },
      ...reduxMotherStateOptions,
    ],
    [reduxMotherStateOptions, translations],
  );

  const guardianStateOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectState || "Select State",
      },
      ...reduxGuardianStateOptions,
    ],
    [reduxGuardianStateOptions, translations],
  );

  const genderOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectGender || "Select Gender",
      },
      ...GENDER_OPTIONS.filter((opt) => opt.value !== ""),
    ],
    [translations],
  );

  // Form data state
  const [formData, setFormData] = useState({
    // Student Information
    studentViewId: null,
    rollId: "",
    emisId: "",
    firstName: "",
    middleName: "",
    lastName: "",
    emailAddress: "",
    dateOfBirth: "",
    aadharNumber: "",
    gender: "",
    class: "",
    section: "",
    religion: "",
    category: "",
    bloodGroup: "",
    medicalCondition: "",
    studentPhotoOriginal: "",
    studentPhotoPath: "",

    // Father Information
    fatherId: null,
    fatherFirstName: "",
    fatherMiddleName: "",
    fatherLastName: "",
    fatherEmail: "",
    fatherPhone: "",
    fatherMobile: "",
    fatherDob: "",
    fatherAnniversary: "",
    fatherPan: "",
    fatherAadhar: "",
    fatherAddress1: "",
    fatherAddress2: "",
    fatherAddress3: "",
    fatherPincode: "",
    fatherCity: "",
    fatherState: "",
    fatherCountry: "",
    fatherOccupation: "",
    fatherIncome: "",
    fatherPhotoOriginal: "",
    fatherPhotoPath: "",

    // Mother Information
    motherId: null,
    motherSameAddress: false,
    motherFirstName: "",
    motherMiddleName: "",
    motherLastName: "",
    motherEmail: "",
    motherPhone: "",
    motherMobile: "",
    motherDob: "",
    motherAnniversary: "",
    motherPan: "",
    motherAadhar: "",
    motherAddress1: "",
    motherAddress2: "",
    motherAddress3: "",
    motherPincode: "",
    motherCity: "",
    motherState: "",
    motherCountry: "",
    motherOccupation: "",
    motherIncome: "",
    motherPhotoOriginal: "",
    motherPhotoPath: "",

    // Is Child Staying with Guardian
    isChildStayingWithGuardian: "",

    // Guardian Information
    guardianId: null,
    guardianFirstName: "",
    guardianMiddleName: "",
    guardianLastName: "",
    guardianEmail: "",
    guardianPhone: "",
    guardianMobile: "",
    guardianDob: "",
    guardianAnniversary: "",
    guardianPan: "",
    guardianAadhar: "",
    guardianAddress1: "",
    guardianAddress2: "",
    guardianAddress3: "",
    guardianPincode: "",
    guardianCity: "",
    guardianState: "",
    guardianCountry: "",
    guardianOccupation: "",
    guardianIncome: "",
    guardianPhotoOriginal: "",
    guardianPhotoPath: "",

    // Emergency Contact 1
    emergency1Id: null,
    emergency1FirstName: "",
    emergency1MiddleName: "",
    emergency1LastName: "",
    emergency1Email: "",
    emergency1Phone: "",
    emergency1RelationType: "",

    // Emergency Contact 2
    emergency2Id: null,
    emergency2FirstName: "",
    emergency2MiddleName: "",
    emergency2LastName: "",
    emergency2Email: "",
    emergency2Phone: "",
    emergency2RelationType: "",

    // Family Doctor
    doctorId: null,
    doctorFirstName: "",
    doctorMiddleName: "",
    doctorLastName: "",
    doctorEmail: "",
    doctorPhone: "",
    doctorHospital: "",
  });

  // Fetch all master data on mount
  useEffect(() => {
    fetchAllMasterData();
    if (editId) {
      fetchStudentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // Fetch all master data using Redux actions
  const fetchAllMasterData = () => {
    dispatch(fetchIncomeOptions());
    dispatch(fetchOccupationOptions());
    dispatch(fetchBloodGroupOptions());
    dispatch(fetchCategoryOptions());
    dispatch(fetchReligionOptions());
    dispatch(fetchRelationOptions());
    dispatch(fetchCountryOptions());
    dispatch(
      fetchClassOptions({
        id: parseInt(school_id),
        academic_year: academic_year,
      }),
    );
  };

  // Fetch sections when class changes
  const handleClassChange = useCallback(
    (classId) => {
      setFormData((prev) => ({
        ...prev,
        class: classId,
        section: "",
      }));

      dispatch(clearSectionOptions());

      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(classId)],
            school_id: parseInt(school_id),
          }),
        );
      }

      setErrors((prev) => {
        if (prev.class) {
          const { class: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    },
    [dispatch, school_id],
  );

  // Helper function to format date from API response
  const formatDateFromApi = (dateString) => {
    if (!dateString) return "";
    try {
      return dayjs(dateString).format("YYYY-MM-DD");
    } catch {
      return "";
    }
  };

  // Populate form data from API response
  const populateFormData = (data) => {
    const studentDetails = data?.student_details || {};
    const fatherDetails = data?.father_details || {};
    const motherDetails = data?.mother_details || {};
    const guardianDetails = data?.guardian_details || {};
    const emergencyContacts = data?.emergency_contacts || {};
    const contact1 = emergencyContacts.contact_1 || {};
    const contact2 = emergencyContacts.contact_2 || {};
    const doctorDetails = data?.family_doctor_details || {};

    const hasGuardian = data?.guardian_details?.is_guardian;

    setFormData((prev) => ({
      ...prev,
      // Store the id from view API for delete (API returns it as
      // student_id — csm.id AS student_id; keep .id as first choice)
      studentViewId: studentDetails.id || studentDetails.student_id || null,

      // Student Information
      rollId: studentDetails.roll_id || "",
      emisId: studentDetails.emis_id || "",
      firstName: studentDetails.first_name || "",
      middleName: studentDetails.middle_name || "",
      lastName: studentDetails.last_name || "",
      emailAddress: studentDetails.email || "",
      dateOfBirth: formatDateFromApi(studentDetails.dob),
      aadharNumber: studentDetails.aadhar_no || "",
      gender: studentDetails.gender || "",
      class: studentDetails.student_class_id?.toString() || "",
      section: studentDetails.student_section || "",
      religion: studentDetails.religion_id?.toString() || "",
      category: studentDetails.caste_id?.toString() || "",
      bloodGroup: studentDetails.blood_group_id?.toString() || "",
      medicalCondition: studentDetails.medical_condition || "",
      studentPhotoOriginal: studentDetails.original_path || "",
      studentPhotoPath: studentDetails.student_photo_path || "",

      // Father Information
      fatherId: fatherDetails.id || null,
      fatherFirstName: fatherDetails.first_name || "",
      fatherMiddleName: fatherDetails.middle_name || "",
      fatherLastName: fatherDetails.last_name || "",
      fatherEmail: fatherDetails.email || "",
      fatherPhone: fatherDetails.phone_no || "",
      fatherMobile: fatherDetails.mobile_no || "",
      fatherDob: formatDateFromApi(fatherDetails.dob),
      fatherAnniversary: formatDateFromApi(fatherDetails.anniversary),
      fatherPan: fatherDetails.pan_no || "",
      fatherAadhar: fatherDetails.aadhar_no || "",
      fatherAddress1: fatherDetails.address1 || "",
      fatherAddress2: fatherDetails.address2 || "",
      fatherAddress3: fatherDetails.address3 || "",
      fatherPincode: fatherDetails.postal_code || "",
      fatherCity: fatherDetails.city || "",
      fatherState: fatherDetails.state_id?.toString() || "",
      fatherCountry: fatherDetails.country_id?.toString() || "",
      fatherOccupation: fatherDetails.occupation_id?.toString() || "",
      fatherIncome: fatherDetails.annual_income_id?.toString() || "",
      fatherPhotoOriginal: fatherDetails.original_path || "",
      fatherPhotoPath: fatherDetails.parent_photo_path || "",

      // Mother Information
      motherSameAddress: data?.isAddress === 1 ? true : false,
      motherId: motherDetails.id || null,
      motherFirstName: motherDetails.first_name || "",
      motherMiddleName: motherDetails.middle_name || "",
      motherLastName: motherDetails.last_name || "",
      motherEmail: motherDetails.email || "",
      motherPhone: motherDetails.phone_no || "",
      motherMobile: motherDetails.mobile_no || "",
      motherDob: formatDateFromApi(motherDetails.dob),
      motherAnniversary: formatDateFromApi(motherDetails.anniversary),
      motherPan: motherDetails.pan_no || "",
      motherAadhar: motherDetails.aadhar_no || "",
      motherAddress1: motherDetails.address1 || "",
      motherAddress2: motherDetails.address2 || "",
      motherAddress3: motherDetails.address3 || "",
      motherPincode: motherDetails.postal_code || "",
      motherCity: motherDetails.city || "",
      motherState: motherDetails.state_id?.toString() || "",
      motherCountry: motherDetails.country_id?.toString() || "",
      motherOccupation: motherDetails.occupation_id?.toString() || "",
      motherIncome: motherDetails.annual_income_id?.toString() || "",
      motherPhotoOriginal: motherDetails.original_path || "",
      motherPhotoPath: motherDetails.parent_photo_path || "",

      // Guardian Information
      isChildStayingWithGuardian: hasGuardian === 1 ? "yes" : "no",
      guardianId: guardianDetails.id || null,
      guardianFirstName: guardianDetails.first_name || "",
      guardianMiddleName: guardianDetails.middle_name || "",
      guardianLastName: guardianDetails.last_name || "",
      guardianEmail: guardianDetails.email || "",
      guardianPhone: guardianDetails.land_line_no || "",
      guardianMobile: guardianDetails.mobile_no || "",
      guardianDob: formatDateFromApi(guardianDetails.dob),
      guardianAnniversary: formatDateFromApi(guardianDetails.anniversary),
      guardianPan: guardianDetails.pan_no || "",
      guardianAadhar: guardianDetails.aadhar_no || "",
      guardianAddress1: guardianDetails.address1 || "",
      guardianAddress2: guardianDetails.address2 || "",
      guardianAddress3: guardianDetails.address3 || "",
      guardianPincode: guardianDetails.postal_code || "",
      guardianCity: guardianDetails.city || "",
      guardianState: guardianDetails.state_id?.toString() || "",
      guardianCountry: guardianDetails.country_id?.toString() || "",
      guardianOccupation: guardianDetails.occupation_id?.toString() || "",
      guardianIncome: guardianDetails.annual_income_id?.toString() || "",
      guardianPhotoOriginal: guardianDetails.original_path || "",
      guardianPhotoPath: guardianDetails.parent_photo_path || "",

      // Emergency Contact 1
      emergency1Id: contact1.contact_1_id || null,
      emergency1FirstName: contact1.first_name || "",
      emergency1MiddleName: contact1.middle_name || "",
      emergency1LastName: contact1.last_name || "",
      emergency1Email: contact1.email || "",
      emergency1Phone: contact1.phone_no?.toString() || "",
      emergency1RelationType: contact1.relation_id?.toString() || "",

      // Emergency Contact 2
      emergency2Id: contact2.contact_2_id || null,
      emergency2FirstName: contact2.first_name || "",
      emergency2MiddleName: contact2.middle_name || "",
      emergency2LastName: contact2.last_name || "",
      emergency2Email: contact2.email || "",
      emergency2Phone: contact2.phone_no?.toString() || "",
      emergency2RelationType: contact2.relation_id?.toString() || "",

      // Family Doctor
      doctorId: doctorDetails.family_doctor_id || null,
      doctorFirstName: doctorDetails.first_name || "",
      doctorMiddleName: doctorDetails.middle_name || "",
      doctorLastName: doctorDetails.last_name || "",
      doctorEmail: doctorDetails.email || "",
      doctorPhone: doctorDetails.phone_no?.toString() || "",
      doctorHospital: doctorDetails.hospital || "",
    }));

    // Fetch states if countries are set using single Redux action
    if (fatherDetails.country_id) {
      dispatch(
        fetchStateOptions({
          countryId: fatherDetails.country_id,
          stateType: STATE_TYPES.FATHER,
        }),
      );
    }
    if (motherDetails.country_id) {
      dispatch(
        fetchStateOptions({
          countryId: motherDetails.country_id,
          stateType: STATE_TYPES.MOTHER,
        }),
      );
    }
    if (guardianDetails?.country_id) {
      dispatch(
        fetchStateOptions({
          countryId: guardianDetails.country_id,
          stateType: STATE_TYPES.GUARDIAN,
        }),
      );
    }

    // Fetch sections for the selected class
    if (studentDetails.student_class_id) {
      dispatch(
        fetchSectionOptions({
          id: [parseInt(studentDetails.student_class_id)],
          school_id: parseInt(school_id),
        }),
      );
    }
  };

  // Fetch student data using the API
  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.post("/school/studentView", {
        student_id: parseInt(editId),
      });
      if (response.data?.status) {
        populateFormData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching student data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    let newValue = value;

    let fieldError = "";

    if (
      name === "emailAddress" ||
      name === "emergency1Email" ||
      name === "fatherEmail" ||
      name === "motherEmail" ||
      name === "emergency2Email" ||
      name === "doctorEmail"
    ) {
      newValue = value.trim();

      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

      if (!newValue) {
        fieldError = "Email address is required.";
      } else if (!emailRegex.test(newValue)) {
        fieldError = "Please enter a valid email address.";
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: fieldError,
    }));
  }, []);

  const handleNumericInput = useCallback((e, fieldName, maxLength = 10) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, maxLength);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    setErrors((prev) => {
      if (prev[fieldName]) {
        const { [fieldName]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handlePanChange = useCallback((e, fieldName) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    let formatted = "";

    for (let i = 0; i < raw.length && i < 10; i++) {
      if (i < 5) {
        if (/[A-Z]/.test(raw[i])) formatted += raw[i];
      } else if (i < 9) {
        if (/[0-9]/.test(raw[i])) formatted += raw[i];
      } else {
        if (/[A-Z]/.test(raw[i])) formatted += raw[i];
      }
    }

    setFormData((prev) => ({
      ...prev,
      [fieldName]: formatted,
    }));

    setErrors((prev) => {
      if (prev[fieldName]) {
        const { [fieldName]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleDropdownChange = useCallback(
    (name, selectedValue) => {
      const value =
        typeof selectedValue === "object" && selectedValue !== null
          ? selectedValue.value
          : selectedValue;

      if (name === "class") {
        handleClassChange(value);
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (name === "fatherCountry") {
        dispatch(clearStateOptions(STATE_TYPES.FATHER));
        setFormData((prev) => ({ ...prev, fatherState: "" }));
        if (value) {
          dispatch(
            fetchStateOptions({
              countryId: value,
              stateType: STATE_TYPES.FATHER,
            }),
          );
        }
      }
      if (name === "motherCountry") {
        dispatch(clearStateOptions(STATE_TYPES.MOTHER));
        setFormData((prev) => ({ ...prev, motherState: "" }));
        if (value) {
          dispatch(
            fetchStateOptions({
              countryId: value,
              stateType: STATE_TYPES.MOTHER,
            }),
          );
        }
      }
      if (name === "guardianCountry") {
        dispatch(clearStateOptions(STATE_TYPES.GUARDIAN));
        setFormData((prev) => ({ ...prev, guardianState: "" }));
        if (value) {
          dispatch(
            fetchStateOptions({
              countryId: value,
              stateType: STATE_TYPES.GUARDIAN,
            }),
          );
        }
      }

      setErrors((prev) => {
        if (prev[name]) {
          const { [name]: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    },
    [dispatch, handleClassChange],
  );

  const handleDateChange = useCallback((name, date) => {
    setFormData((prev) => ({
      ...prev,
      [name]: date ? dayjs(date).format("YYYY-MM-DD") : "",
    }));

    setErrors((prev) => {
      if (prev[name]) {
        const { [name]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleFileUpload = useCallback(
    async (file, originalFieldName, pathFieldName, typeFolder) => {
      if (!file) {
        setFormData((prev) => ({
          ...prev,
          [originalFieldName]: "",
          [pathFieldName]: "",
        }));
        setErrors((prev) => ({
          ...prev,
          [originalFieldName]: "",
        }));
        return;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];

      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [originalFieldName]: "Only jpg, jpeg, png, gif files are allowed",
        }));
        return;
      }

      try {
        const formDataPayload = new FormData();
        formDataPayload.append("content_image", file);
        formDataPayload.append("type", typeFolder);
        formDataPayload.append("school_id", school_id);

        const response = await AxiosInstance.post(
          "/content/upload",
          formDataPayload,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        if (Array.isArray(response.data) && response.data.length > 0) {
          const uploadedFile = response.data[0];

          setFormData((prev) => ({
            ...prev,
            [originalFieldName]: uploadedFile.original_name,
            [pathFieldName]: uploadedFile.upload_path,
          }));

          setErrors((prev) => ({
            ...prev,
            [originalFieldName]: "",
          }));
        } else {
          throw new Error("Invalid upload response");
        }
      } catch (error) {
        console.error("File upload error:", error);
        setErrors((prev) => ({
          ...prev,
          [originalFieldName]: "Failed to upload file. Please try again.",
        }));
      }
    },
    [school_id],
  );

  const handleStudentPhotoChange = useCallback(
    (file) =>
      handleFileUpload(file, "studentPhotoOriginal", "studentPhotoPath", "StudentPhoto"),
    [handleFileUpload],
  );

  const handleFatherPhotoChange = useCallback(
    (file) => handleFileUpload(file, "fatherPhotoOriginal", "fatherPhotoPath", "FatherPhoto"),
    [handleFileUpload],
  );

  const handleMotherPhotoChange = useCallback(
    (file) => handleFileUpload(file, "motherPhotoOriginal", "motherPhotoPath", "MotherPhoto"),
    [handleFileUpload],
  );

  const handleGuardianPhotoChange = useCallback(
    (file) =>
      handleFileUpload(file, "guardianPhotoOriginal", "guardianPhotoPath", "GuardianPhoto"),
    [handleFileUpload],
  );

  const handleCheckboxChange = useCallback(
    (e) => {
      const { name, checked } = e.target;

      setFormData((prev) => {
        const newData = { ...prev, [name]: checked };

        if (name === "motherSameAddress" && checked) {
          newData.motherAddress1 = prev.fatherAddress1;
          newData.motherAddress2 = prev.fatherAddress2;
          newData.motherAddress3 = prev.fatherAddress3;
          newData.motherPincode = prev.fatherPincode;
          newData.motherCity = prev.fatherCity;
          newData.motherState = prev.fatherState;
          newData.motherCountry = prev.fatherCountry;

          if (prev.fatherCountry) {
            dispatch(
              fetchStateOptions({
                countryId: prev.fatherCountry,
                stateType: STATE_TYPES.MOTHER,
              }),
            );
          }
        }

        return newData;
      });
    },
    [dispatch],
  );

  const handleRadioChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => {
      if (prev[name]) {
        const { [name]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleMobileChange = useCallback((e, fieldName) => {
    const value = formatMobileInput(e.target.value);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    setErrors((prev) => {
      if (prev[fieldName]) {
        const { [fieldName]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleAadharChange = useCallback((e, fieldName) => {
    const value = formatAadharInput(e.target.value);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    setErrors((prev) => {
      if (prev[fieldName]) {
        const { [fieldName]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handlePincodeChange = useCallback((e, fieldName) => {
    const value = formatPincodeInput(e.target.value);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    setErrors((prev) => {
      if (prev[fieldName]) {
        const { [fieldName]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // Handle delete button click — show confirmation modal
  const handleDeleteClick = () => {
    setDeleteModalState({ open: true });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    setDeleteModalState({ open: false });

    if (!formData.studentViewId) {
      setModalState({
        open: true,
        type: "error",
        message: "Student ID not found. Cannot delete.",
        redirectPath: "",
      });
      return;
    }

    setDeleting(true);
    try {
      const response = await AxiosInstance.post("/school/delete-student", {
        id: formData.studentViewId,
      });

      if (response?.data?.status === true) {
        setModalState({
          open: true,
          type: "success",
          message: response?.data?.message || "Student deleted successfully!",
          redirectPath: -1,
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            "Failed to delete student. Please try again.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      const serverError =
        error?.response?.data?.message ||
        "Failed to delete student. Please try again.";
      setModalState({
        open: true,
        type: "error",
        message: serverError,
        redirectPath: "",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteModalState({ open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateStudentForm(formData);

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    if (formData.emisId && !/^\d{1,10}$/.test(formData.emisId)) {
      validationErrors.emisId =
        translations.labels?.emisNumericOnly ||
        "EMIS number must contain only numeric values (max 10 digits).";
    }

    if (formData.rollId && !/^\d{1,10}$/.test(formData.rollId)) {
      validationErrors.rollId =
        translations.labels?.rollIdNumericOnly ||
        "Roll ID must contain only numeric values (max 10 digits).";
    }

    if (formData.fatherPhone && !/^\d{10}$/.test(formData.fatherPhone)) {
      validationErrors.fatherPhone =
        translations.labels?.phoneValidation ||
        "Please enter a valid 10-digit phone number.";
    }

    if (formData.fatherMobile && !/^\d{10}$/.test(formData.fatherMobile)) {
      validationErrors.fatherMobile =
        translations.labels?.phoneValidation ||
        "Please enter a valid 10-digit phone number.";
    }

    if (formData.motherPhone && !/^\d{10}$/.test(formData.motherPhone)) {
      validationErrors.motherPhone =
        translations.labels?.phoneValidation ||
        "Please enter a valid 10-digit phone number.";
    }

    if (formData.motherMobile && !/^\d{10}$/.test(formData.motherMobile)) {
      validationErrors.motherMobile =
        translations.labels?.phoneValidation ||
        "Please enter a valid 10-digit phone number.";
    }

    if (
      formData.isChildStayingWithGuardian === "yes" &&
      formData.guardianPhone &&
      !/^\d{10}$/.test(formData.guardianPhone)
    ) {
      validationErrors.guardianPhone =
        translations.labels?.phoneValidation ||
        "Please enter a valid 10-digit phone number.";
    }

    if (
      formData.isChildStayingWithGuardian === "yes" &&
      formData.guardianMobile &&
      !/^\d{10}$/.test(formData.guardianMobile)
    ) {
      validationErrors.guardianMobile =
        translations.labels?.phoneValidation ||
        "Please enter a valid 10-digit phone number.";
    }

    if (
      formData.emergency1Phone &&
      !/^\d{10}$/.test(formData.emergency1Phone)
    ) {
      validationErrors.emergency1Phone =
        translations.labels?.phoneValidation ||
        "Please enter a valid 10-digit phone number.";
    }

    if (
      formData.emergency2Phone &&
      !/^\d{10}$/.test(formData.emergency2Phone)
    ) {
      validationErrors.emergency2Phone =
        translations.labels?.phoneValidation ||
        "Please enter a valid 10-digit phone number.";
    }

    if (formData.doctorPhone && !/^\d{10}$/.test(formData.doctorPhone)) {
      validationErrors.doctorPhone =
        translations.labels?.phoneValidation ||
        "Please enter a valid 10-digit phone number.";
    }

    if (formData.fatherPan && !panRegex.test(formData.fatherPan)) {
      validationErrors.fatherPan =
        translations.labels?.panValidation ||
        "Please enter a valid PAN card number (e.g., ABCDE1234F).";
    }

    if (formData.motherPan && !panRegex.test(formData.motherPan)) {
      validationErrors.motherPan =
        translations.labels?.panValidation ||
        "Please enter a valid PAN card number (e.g., ABCDE1234F).";
    }

    if (
      formData.isChildStayingWithGuardian === "yes" &&
      formData.guardianPan &&
      !panRegex.test(formData.guardianPan)
    ) {
      validationErrors.guardianPan =
        translations.labels?.panValidation ||
        "Please enter a valid PAN card number (e.g., ABCDE1234F).";
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      console.error("Validation Errors:", validationErrors);

      const firstErrorField = document.querySelector('[class*="error"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setSubmitting(true);

    try {
      const isGuardian = formData.isChildStayingWithGuardian === "yes" ? 1 : 0;

      const payload = {
        stud_id: editId ? parseInt(editId) : null,
        academic_year: parseInt(academic_year_id),
        roll_id: parseInt(formData.rollId) || null,
        client_id: parseInt(client_id),
        school_id: parseInt(school_id),
        emis_id: parseInt(formData.emisId) || null,
        first_name: formData.firstName,
        middle_name: formData.middleName || "",
        last_name: formData.lastName,
        email: formData.emailAddress,
        gender: formData.gender,
        blood_group: formData.bloodGroup || null,
        dob: formData.dateOfBirth,
        aadhar_no: formData.aadharNumber || null,
        student_class: formData.class,
        student_section: formData.section,
        religion: parseInt(formData.religion) || null,
        medical_condition: formData.medicalCondition || null,
        caste: parseInt(formData.category) || null,
        orginal_path: formData.studentPhotoOriginal || null,
        student_photo_path: formData.studentPhotoPath || null,
        created_by: parseInt(user_id),
        is_guardian: isGuardian,
        isAddress: formData.motherSameAddress ? 1 : 0,
      };

      if (editId) {
        payload.stud_id = parseInt(editId);
      }

      let guardianInfo = null;
      if (formData.isChildStayingWithGuardian === "yes") {
        guardianInfo = {
          first_name: formData.guardianFirstName,
          middle_name: formData.guardianMiddleName || "",
          last_name: formData.guardianLastName,
          email: formData.guardianEmail,
          mobile_no: formData.guardianMobile,
          dob: formData.guardianDob || null,
          aadhar_number: formData.guardianAadhar || null,
          pan_number: formData.guardianPan || null,
          occupation: parseInt(formData.guardianOccupation) || null,
          annual_income: parseInt(formData.guardianIncome) || null,
          address1: formData.guardianAddress1,
          address2: formData.guardianAddress2 || null,
          address3: formData.guardianAddress3 || null,
          anniversary: formData.guardianAnniversary || null,
          postal_code: formData.guardianPincode,
          city: formData.guardianCity,
          state: parseInt(formData.guardianState) || null,
          country: parseInt(formData.guardianCountry) || null,
          parent_photo_path: formData.guardianPhotoPath || null,
          original_path: formData.guardianPhotoOriginal || null,
        };
      } else {
        guardianInfo = null;
      }

      if (
        editId &&
        formData.guardianId &&
        formData.isChildStayingWithGuardian === "yes"
      ) {
        guardianInfo.id = formData.guardianId;
      }

      payload.guardian_information = guardianInfo;

      const fatherInfo = {
        first_name: formData.fatherFirstName,
        middle_name: formData.fatherMiddleName || "",
        last_name: formData.fatherLastName,
        email: formData.fatherEmail,
        mobile_no: formData.fatherMobile,
        phone_no: formData.fatherPhone,
        dob: formData.fatherDob || null,
        aadhar_number: formData.fatherAadhar || null,
        pan_number: formData.fatherPan || null,
        occupation: parseInt(formData.fatherOccupation) || null,
        annual_income: parseInt(formData.fatherIncome) || null,
        address1: formData.fatherAddress1,
        address2: formData.fatherAddress2 || null,
        address3: formData.fatherAddress3 || null,
        anniversary: formData.fatherAnniversary || null,
        postal_code: formData.fatherPincode,
        city: formData.fatherCity,
        state: parseInt(formData.fatherState) || null,
        country: parseInt(formData.fatherCountry) || null,
        parent_photo_path: formData.fatherPhotoPath || null,
        original_path: formData.fatherPhotoOriginal || null,
      };

      fatherInfo.id = formData.fatherId;

      payload.father_information = fatherInfo;

      const motherInfo = {
        first_name: formData.motherFirstName,
        middle_name: formData.motherMiddleName || "",
        last_name: formData.motherLastName,
        email: formData.motherEmail,
        mobile_no: formData.motherMobile,
        phone_no: formData.motherPhone,
        dob: formData.motherDob || null,
        aadhar_number: formData.motherAadhar || null,
        pan_number: formData.motherPan || null,
        occupation: parseInt(formData.motherOccupation) || null,
        annual_income: parseInt(formData.motherIncome) || null,
        address1: formData.motherAddress1,
        address2: formData.motherAddress2 || null,
        address3: formData.motherAddress3 || null,
        anniversary: formData.motherAnniversary || null,
        postal_code: formData.motherPincode,
        city: formData.motherCity,
        state: parseInt(formData.motherState) || null,
        country: parseInt(formData.motherCountry) || null,
        parent_photo_path: formData.motherPhotoPath || null,
        original_path: formData.motherPhotoOriginal || null,
      };

      motherInfo.id = formData.motherId;

      payload.mother_information = motherInfo;

      const contact1 = {
        first_name: formData.emergency1FirstName,
        middle_name: formData.emergency1MiddleName || "",
        last_name: formData.emergency1LastName,
        email: formData.emergency1Email,
        phone_number: formData.emergency1Phone,
        relation: parseInt(formData.emergency1RelationType) || null,
      };

      if (editId && formData.emergency1Id) {
        contact1.id = formData.emergency1Id;
      }

      payload.contact_no1 = contact1;

      if (formData.emergency2FirstName || formData.emergency2Phone) {
        const contact2 = {
          first_name: formData.emergency2FirstName || "",
          middle_name: formData.emergency2MiddleName || "",
          last_name: formData.emergency2LastName || "",
          email: formData.emergency2Email || "",
          phone_number: formData.emergency2Phone || "",
          relation: parseInt(formData.emergency2RelationType) || null,
        };

        if (editId && formData.emergency2Id) {
          contact2.id = formData.emergency2Id;
        }

        payload.contact_no2 = contact2;
      }

      const doctor = {
        first_name: formData.doctorFirstName || "",
        middle_name: formData.doctorMiddleName || "",
        last_name: formData.doctorLastName || "",
        phone_number: formData.doctorPhone || "",
        email: formData.doctorEmail || "",
        hospital: formData.doctorHospital || "",
      };

      if (editId && formData.doctorId) {
        doctor.id = formData.doctorId;
      }

      payload.family_doctor = doctor;

      const response = editId
        ? await AxiosInstance.put("/school/student/update", payload)
        : await AxiosInstance.post("/school/student/create", payload);

      if (response.data?.success || response.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message || editId
              ? "Student updated successfully!"
              : "Student created successfully!",
          redirectPath: "/user/student",
        });
      } else {
        setModalState({
          open: true,
          type: "failed",
          message:
            response.data?.message || editId
              ? "Student updated failed!"
              : "Student created failed!",
          redirectPath: "/user/student",
        });
        throw new Error(response.data?.message || "Failed to save student");
      }
    } catch (error) {
      console.error("Error saving student:", error);

      setModalState({
        open: true,
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to save student. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaUserGraduate />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.student || "Student"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{translations.labels?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      if (redirectPath === -1) {
        navigate(-1);
      } else {
        navigate(redirectPath);
      }
    }
  };

  return (
    <>
      {/* Success/Error Modal */}
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      {/* Delete Confirmation Modal */}
      <PopupModal
        open={deleteModalState.open}
        type="warning"
        title="Confirm Delete"
        message="Are you sure you want to delete this student? This action cannot be undone."
        onOk={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      <div className={styles.container}>
        <form onSubmit={handleSubmit}>
          {/* ==================== STUDENT SECTION ==================== */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaUserGraduate />
            </div>
            <h1 className={styles.headerTitle}>
              {translations.labels?.student || "Student"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.rollId || "Roll ID"}
                name="rollId"
                type="text"
                value={formData.rollId}
                onChange={(e) => handleNumericInput(e, "rollId", 10)}
                placeholder={
                  translations.labels?.rollIdPlaceholder || "Enter Roll ID"
                }
                maxLength={10}
                error={errors.rollId}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.emisId || "EMIS ID"}
                name="emisId"
                type="text"
                value={formData.emisId}
                onChange={(e) => handleNumericInput(e, "emisId", 10)}
                placeholder={
                  translations.labels?.emisIdPlaceholder || "Enter EMIS ID"
                }
                maxLength={10}
                error={errors.emisId}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.firstName || "First Name"}
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.firstNamePlaceholder ||
                  "Enter First Name"
                }
                required
                error={errors.firstName}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.middleName || "Middle Name"}
                name="middleName"
                type="text"
                value={formData.middleName}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.middleNamePlaceholder ||
                  "Enter Middle Name"
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.lastName || "Last Name"}
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.lastNamePlaceholder || "Enter Last Name"
                }
                required
                error={errors.lastName}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.emailAddress || "Email Address"}
                name="emailAddress"
                type="text"
                value={formData.emailAddress}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.emailPlaceholder || "Enter Email Address"
                }
                required
                error={errors.emailAddress}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.dateOfBirth || "Date of Birth"}
                name="dateOfBirth"
                value={
                  formData.dateOfBirth ? dayjs(formData.dateOfBirth) : null
                }
                onChange={(date) => handleDateChange("dateOfBirth", date)}
                placeholder={
                  translations.labels?.dateOfBirthPlaceholder ||
                  "Select Date of Birth"
                }
                required
                error={errors.dateOfBirth}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.aadharNumber || "Aadhar Number"}
                name="aadharNumber"
                type="text"
                value={formData.aadharNumber}
                onChange={(e) => handleAadharChange(e, "aadharNumber")}
                placeholder={
                  translations.labels?.aadharNumberPlaceholder ||
                  "Enter Aadhar Number"
                }
                maxLength={12}
                error={errors.aadharNumber}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.gender || "Gender"}
                name="gender"
                value={formData.gender}
                onChange={(value) => handleDropdownChange("gender", value)}
                options={genderOptions}
                placeholder={
                  translations.labels?.selectGender || "Select Gender"
                }
                required
                error={errors.gender}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.class || "Class"}
                name="class"
                value={formData.class}
                onChange={(value) => handleDropdownChange("class", value)}
                options={classOptions}
                placeholder={translations.labels?.selectClass || "Select Class"}
                required
                error={errors.class}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.section || "Section"}
                name="section"
                value={formData.section}
                onChange={(value) => handleDropdownChange("section", value)}
                options={sectionOptions}
                placeholder={
                  translations.labels?.selectSection || "Select Section"
                }
                required
                error={errors.section}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.religion || "Religion"}
                name="religion"
                value={formData.religion}
                onChange={(value) => handleDropdownChange("religion", value)}
                options={religionOptions}
                placeholder={
                  translations.labels?.selectReligion || "Select Religion"
                }
                required
                error={errors.religion}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.category || "Category/Caste"}
                name="category"
                value={formData.category}
                onChange={(value) => handleDropdownChange("category", value)}
                options={categoryOptions}
                placeholder={
                  translations.labels?.selectCategory || "Select Category"
                }
                required
                error={errors.category}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.bloodGroup || "Blood Group"}
                name="bloodGroup"
                value={formData.bloodGroup}
                onChange={(value) => handleDropdownChange("bloodGroup", value)}
                options={bloodGroupOptions}
                placeholder={
                  translations.labels?.selectBloodGroup || "Select Blood Group"
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={
                  translations.labels?.medicalCondition || "Medical Condition"
                }
                name="medicalCondition"
                type="text"
                value={formData.medicalCondition}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.medicalConditionPlaceholder ||
                  "Enter Medical Condition"
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <CustomFileInput
                    label={translations.labels?.studentPhoto || "Student Photo"}
                    name="studentPhoto"
                    value={formData.studentPhotoOriginal || null}
                    onChange={handleStudentPhotoChange}
                    accept="image/*"
                    placeholder={
                      formData.studentPhotoOriginal
                        ? formData.studentPhotoOriginal
                        : "No file chosen"
                    }
                    buttonText="Choose File"
                    error={errors.studentPhotoOriginal}
                  />
                </div>
                {formData.studentPhotoPath && (
                  <BlobImage
                    path={formData.studentPhotoPath}
                    alt="Student photo"
                    style={{
                      width: 72,
                      height: 72,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #dcdcdc",
                      marginTop: 24,
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Parent Information Component */}
          <ParentInformation
            formData={formData}
            errors={errors}
            handleInputChange={handleInputChange}
            handleDropdownChange={handleDropdownChange}
            handleDateChange={handleDateChange}
            handleMobileChange={handleMobileChange}
            handleAadharChange={handleAadharChange}
            handlePincodeChange={handlePincodeChange}
            handleCheckboxChange={handleCheckboxChange}
            handleRadioChange={handleRadioChange}
            handleFatherPhotoChange={handleFatherPhotoChange}
            handleMotherPhotoChange={handleMotherPhotoChange}
            handleGuardianPhotoChange={handleGuardianPhotoChange}
            handleNumericInput={handleNumericInput}
            handlePanChange={handlePanChange}
            countryOptions={countryOptions}
            fatherStateOptions={fatherStateOptions}
            motherStateOptions={motherStateOptions}
            guardianStateOptions={guardianStateOptions}
            occupationOptions={occupationOptions}
            incomeOptions={incomeOptions}
            styles={styles}
            radioCheckboxStyles={radioCheckboxStyles}
            translations={translations}
          />

          {/* Emergency Contact Component */}
          <EmergencyContact
            formData={formData}
            errors={errors}
            handleInputChange={handleInputChange}
            handleDropdownChange={handleDropdownChange}
            handleMobileChange={handleMobileChange}
            handleNumericInput={handleNumericInput}
            relationTypeOptions={relationTypeOptions}
            styles={styles}
            translations={translations}
          />

          {/* ==================== BUTTONS ==================== */}
          <div className={styles.buttonContainer}>
            {editId && roleId !== 3 && (
              <CustomButton
                type="button"
                variant="contained"
                className={styles.deleteButton}
                fullWidth={false}
                onClick={handleDeleteClick}
                disabled={deleting}
                loading={deleting}
                loadingText={translations.labels?.deleting || "Deleting..."}
              >
                {translations.labels?.delete || "Delete"}
              </CustomButton>
            )}

            {roleId !== 3 && (
              <CustomButton
                type="submit"
                variant="contained"
                className={styles.submitButton}
                fullWidth={false}
                loading={submitting}
                loadingText={
                  editId
                    ? translations.labels?.updating || "Updating..."
                    : translations.labels?.saving || "Saving..."
                }
              >
                {editId
                  ? translations.labels?.update || "Update"
                  : translations.labels?.save || "Save"}
              </CustomButton>
            )}

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancel || "Cancel"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default StudentCreate;
